import React from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { API } from 'constants';
import client from '../../../services/library-api';

import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useOverlayState } from '../../../hooks/useOverlayState';
import { useFetchDataOnLoad } from '../../../hooks/useFetchDataOnLoad';
import { useManageSearchParams } from '../../../hooks/useManageSearchParams';
import { useDefaultSearchParams } from '../../../hooks/useDefaultSearchParams';
import { useHandlePageDataLoadError } from '../../../hooks/useHandlePageDataLoadError';
import { useFetchOptionsForPaginatedSelect } from '../../../hooks/useFetchOptionsForPaginatedSelect';

import { CONFIGURE_TAB } from '../../../constants/configure';

import { capitalizeFirstLetter } from '../../../services/strings';

import { Tabs } from '../../../design-system';
import DeletePlaybookModal from './DeletePlaybookModal/DeletePlaybookModal';
import PlaybookPageMainContent from './PlaybookPageMainContent/PlaybookPageMainContent';
import PlaybookMoreOptionsPopup from './PlaybookMoreOptionsPopup/PlaybookMoreOptionsPopup';
import ConfigureDetailPageLayout from '../ConfigureDetailPageLayout/ConfigureDetailPageLayout';

export const TAB = { settings: 'settings', scenarios: 'scenarios', prompts: 'prompts' };

const tabs = [TAB.settings, TAB.scenarios, TAB.prompts];
const DEFAULT_TAB = TAB.settings;

const formattedTabs = tabs.map((key) => ({ key, name: capitalizeFirstLetter(key) }));

const PlaybookDetailPage = () => {
    const { playbookId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const location = useLocation();
    const navigate = useNavigate();

    const activeTab = searchParams.get('tab');
    const activeTabIndex = Math.max(tabs.indexOf(activeTab), 0);

    const backLinkHref = location.state?.from ?? `/configure?tab=${CONFIGURE_TAB.playbooks}`;

    const { setSearchParam } = useManageSearchParams();
    const { handlePageDataLoadError } = useHandlePageDataLoadError();

    const { data: playbookDetail, setData: setPlaybookDetail } = useFetchDataOnLoad({
        client,
        route: `${API.ROUTES.library.playbook}${playbookId}/`,
        searchParams: { shared: true },
        onError: (e) =>
            handlePageDataLoadError({
                e,
                redirectPath: `/configure?tab=${CONFIGURE_TAB.playbooks}`,
                generalErrorHandler: () => navigate(backLinkHref),
            }),
    });

    const isEditable = playbookDetail && !playbookDetail.is_shared;

    const playbookViewsResponse = useFetchOptionsForPaginatedSelect({
        client,
        route: API.ROUTES.library.playbookView,
        searchParams: { playbook: playbookId },
    });

    const {
        isOpened: isDeleteModalOpened,
        open: openDeleteModal,
        close: closeDeleteModal,
    } = useOverlayState();

    useDefaultSearchParams({
        checkPage: false,
        checkSort: false,
        checkTab: true,
        defaultTab: DEFAULT_TAB,
        availableTabs: tabs,
    });

    useDocumentTitle(playbookDetail?.name);

    const renderMoreOptions = ({ onClose }) => {
        return (
            <PlaybookMoreOptionsPopup
                id={playbookDetail.id}
                isEditable={isEditable}
                openConfirmDeleteModal={openDeleteModal}
                onClose={onClose}
            />
        );
    };

    return (
        <ConfigureDetailPageLayout
            name={playbookDetail?.name}
            is_shared={playbookDetail?.is_shared}
            description={playbookDetail?.description}
            isLoading={!playbookDetail}
            backLinkHref={backLinkHref}
            breadcrumbs="Configure / Playbook Overview"
            renderMoreOptions={renderMoreOptions}
        >
            <div className="max-w-max">
                <Tabs
                    tabs={formattedTabs}
                    tabIndex={activeTabIndex}
                    onTabChanged={(tab) => setSearchParam('tab', tab.key)}
                />
            </div>

            <PlaybookPageMainContent
                activeTab={activeTab}
                playbookDetail={playbookDetail}
                setPlaybookDetail={setPlaybookDetail}
                playbookViewsResponse={playbookViewsResponse}
                isEditable={isEditable}
            />

            {isDeleteModalOpened && (
                <DeletePlaybookModal
                    id={playbookDetail.id}
                    name={playbookDetail.name}
                    onDeleteSuccess={() => navigate(backLinkHref, { replace: true })}
                    onClose={closeDeleteModal}
                />
            )}
        </ConfigureDetailPageLayout>
    );
};

export default PlaybookDetailPage;
